"use client";

import LinkLoadingIndicator from "@/components/loader/link-loading-indicator";
import { TextShimmer } from "@/components/text-shimmer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  ChevronDown,
  FileBadge,
  Hotel,
  Loader2Icon,
  MapPinned,
  MoonStar,
  PlaneLanding,
  PlaneTakeoff,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useState, useTransition } from "react";
import {
  getSearchTabFallbackHref,
  isExternalSearchTab,
  resolveSearchTabHref,
} from "./search-tab-navigation";

const TAB_IDS = [
  "flights",
  "hotels",
  "visa",
  "holidays",
  "umrah",
  "airport_services",
] as const;
type TabId = (typeof TAB_IDS)[number];

const CUSTOM_UMRAH_HREF = "/umrah/custom";
const UMRAH_MENU_OPTIONS = [
  {
    id: "package",
    label: "Package",
    description: "Ready-made Umrah packages.",
    href: "/umrah",
  },
  {
    id: "custom",
    label: "Custom",
    description: "Start a tailored Umrah request",
    href: CUSTOM_UMRAH_HREF,
  },
] as const;
type UmrahMenuOptionId = (typeof UMRAH_MENU_OPTIONS)[number]["id"];

const TABS = [
  {
    id: "flights",
    label: "Flights",
    mobileLabel: "Flight",
    icon: PlaneTakeoff,
  },
  { id: "hotels", label: "Hotels", mobileLabel: "Hotel", icon: Hotel },
  { id: "visa", label: "Visa", mobileLabel: "Visa", icon: FileBadge },
  {
    id: "holidays",
    label: "Holidays",
    mobileLabel: "Holiday",
    icon: MapPinned,
  },
  { id: "umrah", label: "Umrah", mobileLabel: "Umrah", icon: MoonStar },
  {
    id: "airport_services",
    label: "Airport Services",
    mobileLabel: "Airport",
    icon: PlaneLanding,
  },
] as const;

const TAB_ROUTE_PREFIXES: Partial<Record<TabId, string>> = {
  flights: "/flights",
  hotels: "/hotels",
  visa: "/visa",
  holidays: "/holiday",
  umrah: "/umrah",
  airport_services: "https://sohi.com.bd/",
};

export const SEARCH_ENGINE_TAB_LINKS: Array<{
  id: TabId;
  label: string;
  href: string;
  external: boolean;
}> = TABS.map((item) => ({
  id: item.id,
  label: item.label,
  href: getSearchTabFallbackHref(item.id),
  external: isExternalSearchTab(item.id),
}));

const tabParser = parseAsStringLiteral(TAB_IDS).withDefault("flights");

type Props = {
  variant?: "hero" | "header";
  className?: string;
};

export function SearchEngineTab({ variant = "hero", className }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [pendingTabId, setPendingTabId] = useState<TabId | null>(null);
  const [headerUmrahMenuOpen, setHeaderUmrahMenuOpen] = useState(false);
  const [mobileUmrahMenuOpen, setMobileUmrahMenuOpen] = useState(false);
  const [desktopUmrahMenuOpen, setDesktopUmrahMenuOpen] = useState(false);

  const isDashboard = pathname.startsWith("/dashboard");
  const [tab, setTab] = useQueryState("tab", tabParser);
  const isUmrahLoading = isPending && pendingTabId === "umrah";

  function navigateToHref(href: string, tabId: TabId) {
    if (isPending) return;

    setPendingTabId(tabId);
    startTransition(() => {
      router.push(href);
    });
  }

  function handleTabNavigation(tabId: TabId) {
    if (isPending) return;

    if (isExternalSearchTab(tabId)) {
      window.open("https://sohi.com.bd/", "_blank", "noopener,noreferrer");
      return;
    }

    const resolvedHref = resolveSearchTabHref(tabId);
    const fallbackHref = getSearchTabFallbackHref(tabId);
    const isHomeLikeRoute = pathname === "/" || pathname === "/hotels" || pathname === "/holiday";
    const shouldStayOnHome =
      pathname === "/" &&
      resolvedHref === fallbackHref &&
      fallbackHref !== "/umrah" &&
      fallbackHref !== "/holiday";

    setPendingTabId(tabId);

    startTransition(() => {
      if (shouldStayOnHome) {
        void setTab(tabId);
        return;
      }

      router.push(resolvedHref);
    });
  }

  function handleUmrahMenuNavigation(optionId: UmrahMenuOptionId) {
    if (isPending) return;

    setHeaderUmrahMenuOpen(false);
    setMobileUmrahMenuOpen(false);
    setDesktopUmrahMenuOpen(false);

    if (optionId === "custom") {
      navigateToHref(CUSTOM_UMRAH_HREF, "umrah");
      return;
    }

    navigateToHref(resolveSearchTabHref("umrah"), "umrah");
  }

  const activeTab: TabId | undefined = (() => {
    // On the homepage, active is controlled by ?tab=... (works for external tabs too)
    if (pathname === "/") {
      return tab;
    }

    const matchedTab = (
      Object.entries(TAB_ROUTE_PREFIXES) as Array<[TabId, string]>
    ).find(([, routePrefix]) => {
      // External links can never match pathname
      if (routePrefix.startsWith("http")) return false;

      return pathname === routePrefix || pathname.startsWith(`${routePrefix}/`);
    });

    if (matchedTab) return matchedTab[0];

    return undefined;
  })();

  function handleHeroTabClick(tabId: TabId) {
    if (isPending || isUmrahLoading) return;

    if (pathname === "/") {
      if (tabId === "umrah") {
        setPendingTabId(tabId);
        startTransition(() => {
          router.push("/umrah");
        });
        return;
      }

      if (tabId === "holidays") {
        setPendingTabId(tabId);
        startTransition(() => {
          router.push("/holiday");
        });
        return;
      }

      if (isExternalSearchTab(tabId)) {
        window.open("https://sohi.com.bd/", "_blank", "noopener,noreferrer");
        return;
      }

      void setTab(tabId);
      return;
    }

    handleTabNavigation(tabId);
  }

  if (variant === "header") {
    return (
      <div className={cn("flex w-full justify-center gap-2", className)}>
        {TABS.map((item) => {
          const isActive = item.id === activeTab && !isDashboard;
          const href = getSearchTabFallbackHref(item.id);
          const isExternal = isExternalSearchTab(item.id);

          if (item.id === "umrah") {
            return (
              <DropdownMenu
                key={`${item.id}-${pathname}`}
                modal={false}
                open={headerUmrahMenuOpen}
                onOpenChange={(open) => {
                  setHeaderUmrahMenuOpen(isPending ? false : open);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-disabled={isPending}
                    disabled={isPending}
                    className={cn(
                      "group relative flex items-center gap-1 rounded-md px-2 py-2 font-medium transition-colors",
                      "max-w-24 lg:max-w-none truncate",
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                      isPending && "pointer-events-none cursor-not-allowed",
                    )}
                  >
                    <span
                      className={cn(
                        "relative min-w-0 flex-1 truncate whitespace-nowrap",
                        "after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-0.5 after:origin-left after:rounded-full after:bg-primary after:transition-transform after:duration-300 motion-reduce:after:transition-none",
                        isActive
                          ? "after:scale-x-100"
                          : "after:scale-x-0 group-hover:after:scale-x-100",
                      )}
                    >
                      {isUmrahLoading ? (
                        <TextShimmer>{item.label}</TextShimmer>
                      ) : (
                        item.label
                      )}
                    </span>
                    <ChevronDown className="size-4 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={8}
                  className="w-56"
                >
                  {UMRAH_MENU_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.id}
                      disabled={isPending}
                      onSelect={(event) => {
                        event.preventDefault();
                        handleUmrahMenuNavigation(option.id);
                      }}
                      className="flex flex-col items-start gap-0.5"
                    >
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }

          return (
            <Link
              key={item.id}
              aria-disabled={isPending}
              href={href}
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noopener noreferrer" : undefined}
              onClick={(event) => {
                if (isExternal) {
                  return;
                }

                event.preventDefault();
                handleTabNavigation(item.id);
              }}
              className={cn(
                // important bits:
                "relative  rounded-md px-2 py-2 font-medium transition-colors",
                "max-w-20 lg:max-w-none  truncate",
                "after:absolute after:left-3 after:right-3 after:bottom-1 after:h-0.5 after:origin-left after:rounded-full after:bg-primary after:transition-transform after:duration-300 motion-reduce:after:transition-none",
                isActive
                  ? "text-foreground after:scale-x-100"
                  : "text-muted-foreground hover:text-foreground after:scale-x-0 hover:after:scale-x-100",
                isPending && "pointer-events-none cursor-not-allowed",
              )}
            >
              <span className="min-w-0 flex-1 truncate whitespace-nowrap">
                <LinkLoadingIndicator title={item.label} />
              </span>
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)} role="tablist">
      <div className="relative sm:hidden">
        <div className="relative overflow-x-auto scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex min-w-max items-stretch justify-around sm:justify-start gap-1 ">
            {TABS.map((item) => {
              const isActive = item.id === activeTab && !isDashboard;
              const isTabLoading = isPending && pendingTabId === item.id;

              if (item.id === "umrah") {
                return (
                  <DropdownMenu
                    key={`${item.id}-${pathname}`}
                    modal={false}
                    open={mobileUmrahMenuOpen}
                    onOpenChange={(open) => {
                      setMobileUmrahMenuOpen(isPending ? false : open);
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-busy={isTabLoading}
                        aria-haspopup="menu"
                        disabled={isPending}
                        className={cn(
                          "relative flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2.5 text-[11px] font-semibold whitespace-nowrap transition-colors focus:outline-none",
                          isActive
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                          isPending && "cursor-not-allowed opacity-70",
                        )}
                      >
                        <span
                          className={cn(
                            "text-muted-foreground transition-colors",
                            isActive && "text-primary",
                          )}
                        >
                          <item.icon className="size-4" />
                        </span>

                        <span className="flex max-w-18 items-center gap-1 truncate leading-none">
                          <span className="truncate">
                            {isTabLoading ? (
                              <TextShimmer>{item.mobileLabel}</TextShimmer>
                            ) : (
                              item.mobileLabel
                            )}
                          </span>
                          <ChevronDown className="size-3 shrink-0" />
                        </span>

                        <span
                          aria-hidden="true"
                          className={cn(
                            "absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary transition-transform duration-200",
                            isActive ? "scale-x-100" : "scale-x-0",
                          )}
                        />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="center"
                      sideOffset={8}
                      className="w-52"
                    >
                      {UMRAH_MENU_OPTIONS.map((option) => (
                        <DropdownMenuItem
                          key={option.id}
                          disabled={isPending}
                          onSelect={(event) => {
                            event.preventDefault();
                            handleUmrahMenuNavigation(option.id);
                          }}
                          className="flex flex-col items-start gap-0.5"
                        >
                          <span className="font-medium">{option.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-busy={isTabLoading}
                  disabled={isPending}
                  onClick={() => handleHeroTabClick(item.id)}
                  className={cn(
                    "relative flex  flex-col items-center justify-center gap-1 rounded-xl px-2 py-2.5 text-[11px] font-semibold whitespace-nowrap transition-colors focus:outline-none",
                    isActive
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                    isPending && "cursor-not-allowed opacity-70",
                  )}
                >
                  <span
                    className={cn(
                      "text-muted-foreground transition-colors",
                      isActive && "text-primary",
                    )}
                  >
                    {isTabLoading ? (
                      <Loader2Icon className="size-4 animate-spin" />
                    ) : (
                      <item.icon className="size-4" />
                    )}
                  </span>

                  <span className="max-w-18 truncate leading-none">
                    {isTabLoading ? (
                      <TextShimmer>{item.mobileLabel}</TextShimmer>
                    ) : (
                      item.mobileLabel
                    )}
                  </span>

                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary transition-transform duration-200",
                      isActive ? "scale-x-100" : "scale-x-0",
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="hidden overflow-x-auto scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none] sm:block">
        <div className="flex min-w-max items-center gap-1">
          {TABS.map((item) => {
            const isActive = item.id === activeTab && !isDashboard;
            const isTabLoading = isPending && pendingTabId === item.id;

            if (item.id === "umrah") {
              return (
                <DropdownMenu
                  key={`${item.id}-${pathname}`}
                  modal={false}
                  open={desktopUmrahMenuOpen}
                  onOpenChange={(open) => {
                    setDesktopUmrahMenuOpen(isPending ? false : open);
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-busy={isTabLoading}
                      aria-haspopup="menu"
                      disabled={isPending}
                      className={cn(
                        "group relative inline-flex items-center gap-2.5 px-4 py-5 text-sm font-semibold whitespace-nowrap transition-colors focus:outline-none",
                        isActive
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground",
                        isPending && "cursor-not-allowed opacity-70",
                      )}
                    >
                      <span
                        className={cn(
                          "text-muted-foreground transition-colors",
                          isActive && "text-primary",
                        )}
                      >
                        <item.icon className="size-4.5" />
                      </span>

                      <span className="flex items-center gap-1 text-sm">
                        <span
                          className={cn(
                            "relative",
                            "after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-0.5 after:origin-left after:rounded-full after:bg-primary after:transition-transform after:duration-200",
                            isActive
                              ? "after:scale-x-100"
                              : "after:scale-x-0 group-hover:after:scale-x-100",
                          )}
                        >
                          {isTabLoading ? (
                            <TextShimmer>{item.label}</TextShimmer>
                          ) : (
                            item.label
                          )}
                        </span>
                        <ChevronDown className="size-4 shrink-0" />
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="center"
                    sideOffset={8}
                    className="w-56"
                  >
                    {UMRAH_MENU_OPTIONS.map((option) => (
                      <DropdownMenuItem
                        key={option.id}
                        disabled={isPending}
                        onSelect={(event) => {
                          event.preventDefault();
                          handleUmrahMenuNavigation(option.id);
                        }}
                        className="flex flex-col items-start gap-0.5"
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-busy={isTabLoading}
                disabled={isPending}
                onClick={() => handleHeroTabClick(item.id)}
                className={cn(
                  "relative inline-flex items-center gap-2.5 px-4 py-5 text-sm font-semibold whitespace-nowrap transition-colors focus:outline-none",
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                  isPending && "cursor-not-allowed opacity-70",
                )}
              >
                <span
                  className={cn(
                    "text-muted-foreground transition-colors",
                    isActive && "text-primary",
                  )}
                >
                  {isTabLoading ? (
                    <Loader2Icon className="size-4.5 animate-spin" />
                  ) : (
                    <item.icon className="size-4.5" />
                  )}
                </span>

                <span className="text-sm">
                  {isTabLoading ? (
                    <TextShimmer>{item.label}</TextShimmer>
                  ) : (
                    item.label
                  )}
                </span>

                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-primary transition-transform duration-200",
                    isActive ? "scale-x-100" : "scale-x-0",
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
